import React from 'react';
import './BlogPost.css';
import SEOHead from '../../components/SEOHead';
import FAQSchema from '../../components/FAQSchema';

const TroubleshootingPage: React.FC = () => {
  const faqs = [
    {
      question: "Why aren't my trailer lights working?",
      answer: "The most common cause is a bad ground connection. Check the ground wire connection at the frame and vehicle hitch, clean connection points down to bare metal, and tighten securely. Other causes include corroded wiring connections, blown bulbs, or vehicle-side electrical problems. Use a circuit tester to diagnose which component is failing."
    },
    {
      question: "How do I know if my wheel bearings are bad?",
      answer: "Signs of bad wheel bearings include: wheel wobbles or feels loose when rocked at 12 and 6 o'clock positions, hub running excessively hot (too hot to touch comfortably), grinding or growling noise from the wheel while towing, or visible grease leaking from the hub. If you notice any of these symptoms, stop immediately and service the bearings before continuing to tow."
    },
    {
      question: "What causes trailer brakes to lock up or drag?",
      answer: "For surge brakes, the actuator may be stuck in compressed position or the lockout mechanism isn't disengaged. For electric brakes, the controller gain may be set too high. Mechanical causes include over-adjusted brake shoes or seized brakes from corrosion. Also check if the breakaway cable has been pulled out, which activates the emergency brake system."
    },
    {
      question: "Why is my trailer swaying while towing?",
      answer: "Trailer sway is usually caused by improper tongue weight (should be 10-15% of total weight), excessive speed especially in wind, uneven tire pressure, or improper load distribution. Shift the load forward to increase tongue weight, reduce speed, check all tire pressures are equal, and ensure the boat is centered on the trailer. Consider adding a sway control device if problems persist."
    },
    {
      question: "How can I tell if my trailer tires are wearing incorrectly?",
      answer: "Center wear (bald in middle) indicates over-inflation. Edge wear (bald on sides) indicates under-inflation. One-sided wear suggests axle misalignment or bent spindle requiring professional alignment. Cupping or scalloping indicates bad bearings or out-of-balance wheels. Always maintain proper tire pressure and inspect tires before each trip."
    }
  ];

  return (
    <div className="blog-post">
      <SEOHead
        title="Common Boat Trailer Problems & Troubleshooting Guide"
        description="Expert troubleshooting guide for common boat trailer issues. Learn to diagnose and fix problems with lights, brakes, tires, bearings, and more to keep your trailer road-ready."
        keywords="trailer problems, troubleshooting guide, trailer repair, brake issues, light problems, bearing failure, tire problems"
        canonical="https://fastboattrailers.com/tips/troubleshooting-guide"
      />
      <FAQSchema faqs={faqs} />
      <div className="container">
        <article className="blog-article">
          <header className="blog-header">
            <h1>Common Boat Trailer Problems & Troubleshooting</h1>
            <div className="blog-meta">
              <span className="blog-read-time">8 min read</span>
            </div>
          </header>

          <div className="blog-content">
            <p className="blog-intro">
              Even well-maintained trailers can develop problems. Knowing how to quickly diagnose and fix common issues
              can save you time, money, and prevent a ruined boating day. Here's your comprehensive troubleshooting guide.
            </p>

            <h2>Electrical & Lighting Problems</h2>

            <h3>Problem: Trailer Lights Not Working</h3>

            <h4>Possible Causes & Solutions:</h4>

            <p><strong>1. Bad Ground Connection</strong> (most common)</p>
            <ul>
              <li><strong>Symptom:</strong> Lights dim, flicker, or work intermittently</li>
              <li><strong>Check:</strong> Ground wire connection at frame and vehicle hitch</li>
              <li><strong>Fix:</strong> Clean connection points down to bare metal, tighten securely</li>
              <li><strong>Prevention:</strong> Apply dielectric grease to prevent corrosion</li>
            </ul>

            <p><strong>2. Corroded Wiring Connections</strong></p>
            <ul>
              <li><strong>Symptom:</strong> Some lights work, others don't; problems worsen after water immersion</li>
              <li><strong>Check:</strong> Inspect all wire connections and splices for green corrosion</li>
              <li><strong>Fix:</strong> Cut out corroded sections, use waterproof wire connectors, seal with heat shrink</li>
              <li><strong>Prevention:</strong> Use marine-grade wiring and keep connections above water line</li>
            </ul>

            <p><strong>3. Blown Bulbs</strong></p>
            <ul>
              <li><strong>Symptom:</strong> Specific light doesn't work</li>
              <li><strong>Check:</strong> Remove lens and inspect bulb filament</li>
              <li><strong>Fix:</strong> Replace with same wattage bulb or upgrade to LED</li>
              <li><strong>Tip:</strong> LEDs last much longer and resist shock/vibration</li>
            </ul>

            <p><strong>4. Vehicle-Side Problems</strong></p>
            <ul>
              <li><strong>Symptom:</strong> All trailer lights don't work, but you tested them previously</li>
              <li><strong>Check:</strong> Vehicle's trailer connector and fuse</li>
              <li><strong>Fix:</strong> Check vehicle's trailer brake fuse, clean vehicle connector pins</li>
              <li><strong>Test:</strong> Use a circuit tester to verify power at vehicle plug</li>
            </ul>

            <div className="blog-callout">
              <h4>Quick Test</h4>
              <p>
                Connect trailer to vehicle, turn on headlights. Have someone watch while you test: turn signals (both sides),
                brake lights, and running lights. Mark which lights fail before starting diagnosis.
              </p>
            </div>

            <h2>Brake Problems</h2>

            <h3>Problem: Brakes Not Engaging</h3>

            <h4>Electric Brakes:</h4>
            <ul>
              <li><strong>Check brake controller:</strong> Verify it's powered and set correctly (gain setting)</li>
              <li><strong>Check brake magnet:</strong> Should have 3-4 ohms resistance; if infinite, magnet is bad</li>
              <li><strong>Check wiring:</strong> Trace blue wire from connector to each brake assembly</li>
              <li><strong>Check brake adjustment:</strong> May need tightening (see brake maintenance guide)</li>
            </ul>

            <h4>Surge Brakes:</h4>
            <ul>
              <li><strong>Check master cylinder:</strong> Verify brake fluid level, check for leaks</li>
              <li><strong>Check actuator:</strong> Should compress when pushed; if stuck, may need service</li>
              <li><strong>Bleed the system:</strong> Air in brake lines prevents proper engagement</li>
              <li><strong>Check breakaway system:</strong> Pull pin to test emergency brake function</li>
            </ul>

            <h3>Problem: Brakes Locking Up or Dragging</h3>
            <ul>
              <li><strong>Surge brakes:</strong> Actuator may be stuck in compressed position - check lockout isn't engaged</li>
              <li><strong>Electric brakes:</strong> Controller gain set too high - reduce sensitivity</li>
              <li><strong>Mechanical:</strong> Brake shoes may be over-adjusted or seized from corrosion</li>
              <li><strong>Emergency:</strong> If breakaway cable pulled out, emergency brake is activated</li>
            </ul>

            <h2>Wheel Bearing Problems</h2>

            <h3>Problem: Wheel Feels Loose or Wobbles</h3>
            <ul>
              <li><strong>Likely cause:</strong> Worn or improperly adjusted bearings</li>
              <li><strong>Immediate action:</strong> Do NOT tow - risk of wheel coming off</li>
              <li><strong>Check:</strong> Jack up wheel, grasp at 12 and 6 o'clock, try to rock it</li>
              <li><strong>Fix:</strong> Repack bearings and adjust; replace if worn or pitted</li>
            </ul>

            <h3>Problem: Hub Running Hot</h3>
            <ul>
              <li><strong>Safe temp:</strong> Should be warm to touch but not burning hot</li>
              <li><strong>Too tight:</strong> Bearings over-tightened - creates friction and heat</li>
              <li><strong>No grease:</strong> Bearings dry - immediate failure risk</li>
              <li><strong>Bad bearings:</strong> Pitted or damaged bearings generate heat</li>
              <li><strong>Action:</strong> Stop immediately, let cool, do not continue until serviced</li>
            </ul>

            <h3>Problem: Grinding or Growling Noise from Wheel</h3>
            <ul>
              <li><strong>Cause:</strong> Bearing failure in progress</li>
              <li><strong>Severity:</strong> High - can lead to wheel seizure or loss</li>
              <li><strong>Action:</strong> Service bearings immediately; likely need replacement</li>
              <li><strong>Prevention:</strong> Annual bearing service, check after saltwater use</li>
            </ul>

            <h2>Tire Problems</h2>

            <h3>Problem: Rapid or Uneven Tire Wear</h3>

            <p><strong>Center wear (bald in middle):</strong></p>
            <ul>
              <li><strong>Cause:</strong> Over-inflation</li>
              <li><strong>Fix:</strong> Reduce to proper PSI (check sidewall)</li>
            </ul>

            <p><strong>Edge wear (bald on sides):</strong></p>
            <ul>
              <li><strong>Cause:</strong> Under-inflation</li>
              <li><strong>Fix:</strong> Inflate to proper PSI, check for slow leaks</li>
            </ul>

            <p><strong>One-sided wear:</strong></p>
            <ul>
              <li><strong>Cause:</strong> Axle misalignment or bent spindle</li>
              <li><strong>Fix:</strong> Professional alignment check; may need axle replacement</li>
            </ul>

            <p><strong>Cupping or scalloping:</strong></p>
            <ul>
              <li><strong>Cause:</strong> Bad bearings or out-of-balance wheel</li>
              <li><strong>Fix:</strong> Service bearings, check wheel balance</li>
            </ul>

            <h3>Problem: Frequent Flat Tires</h3>
            <ul>
              <li><strong>Slow leak at valve stem:</strong> Replace valve stem or entire valve</li>
              <li><strong>Bead leak:</strong> Clean corrosion from rim bead seat, reseat tire</li>
              <li><strong>Age-related cracking:</strong> Replace tires over 5-6 years old</li>
              <li><strong>Puncture:</strong> Repair or replace depending on location and size</li>
            </ul>

            <h2>Structural & Mechanical Problems</h2>

            <h3>Problem: Trailer Bounces Excessively</h3>
            <ul>
              <li><strong>Leaf springs:</strong> Check for broken leaves, worn shackles, missing U-bolts</li>
              <li><strong>Torsion axles:</strong> May be damaged or worn (cannot be repaired, must replace)</li>
              <li><strong>Shock absorbers:</strong> If equipped, may be worn out</li>
              <li><strong>Tire pressure:</strong> Under-inflated tires cause excessive bounce</li>
            </ul>

            <h3>Problem: Winch Won't Pull Boat</h3>

            <h4>Manual Winch:</h4>
            <ul>
              <li><strong>Frozen gear:</strong> Spray with penetrating oil, work back and forth</li>
              <li><strong>Bent handle:</strong> Replace handle</li>
              <li><strong>Cable/strap issues:</strong> Check for kinks in cable or fraying in strap</li>
            </ul>

            <h4>Electric Winch:</h4>
            <ul>
              <li><strong>No power:</strong> Check circuit breaker, battery connections, fuse</li>
              <li><strong>Motor runs but won't pull:</strong> Stripped gears, needs replacement</li>
              <li><strong>Intermittent operation:</strong> Loose connections, corroded switch</li>
              <li><strong>Remote doesn't work:</strong> Replace remote battery, check wiring</li>
            </ul>

            <h3>Problem: Coupler Won't Latch or Stays Loose</h3>
            <ul>
              <li><strong>Wrong size:</strong> Coupler must match ball size exactly (1-7/8", 2", 2-5/16")</li>
              <li><strong>Worn coupler:</strong> Replace if latch mechanism is damaged</li>
              <li><strong>Dirty mechanism:</strong> Clean and lubricate latch</li>
              <li><strong>Safety pin missing:</strong> Always use safety pin/clip as backup</li>
            </ul>

            <h3>Problem: Trailer Sways While Towing</h3>
            <ul>
              <li><strong>Tongue weight too light:</strong> Should be 10-15% of total weight - shift load forward</li>
              <li><strong>Speed too high:</strong> Reduce speed, especially in wind</li>
              <li><strong>Tire pressure uneven:</strong> Check all tires, inflate equally</li>
              <li><strong>Load distribution:</strong> Ensure boat is centered on trailer</li>
              <li><strong>Consider:</strong> Sway control device or weight-distributing hitch</li>
            </ul>

            <h2>Corrosion Problems</h2>

            <h3>Problem: Rust Spreading on Frame</h3>

            <h4>Galvanized Trailers:</h4>
            <ul>
              <li><strong>Surface rust:</strong> Wire brush, apply cold galvanizing spray</li>
              <li><strong>Deep rust:</strong> May indicate galvanizing failure - needs professional assessment</li>
              <li><strong>Welds rusting:</strong> Normal for galvanized coating to burn off during welding - coat with cold galv</li>
            </ul>

            <h4>Painted Steel:</h4>
            <ul>
              <li><strong>Treatment:</strong> Sand to bare metal, apply rust converter, prime, and paint</li>
              <li><strong>Prevention:</strong> Touch up chips immediately, wash after saltwater</li>
            </ul>

            <h3>Problem: Frozen/Seized Bolts and Hardware</h3>
            <ul>
              <li><strong>Penetrating oil:</strong> Apply liberally, let soak overnight</li>
              <li><strong>Heat:</strong> Use torch carefully (avoid brake lines, wiring)</li>
              <li><strong>Cut and replace:</strong> If stuck bolt isn't critical to strength, cut it off</li>
              <li><strong>Prevention:</strong> Use anti-seize compound on threads, stainless hardware in salt water</li>
            </ul>

            <h2>Loading & Launching Problems</h2>

            <h3>Problem: Boat Won't Load Straight</h3>
            <ul>
              <li><strong>Guide posts missing/damaged:</strong> Install or repair guide bunks</li>
              <li><strong>Uneven bunks:</strong> Adjust bunk height and spacing</li>
              <li><strong>Hull position:</strong> Center keel on trailer before winching</li>
              <li><strong>Current/wind:</strong> Approach ramp at slight angle to compensate</li>
            </ul>

            <h3>Problem: Boat Stuck on Trailer</h3>
            <ul>
              <li><strong>Not deep enough:</strong> Back trailer deeper into water</li>
              <li><strong>Bunks too tight:</strong> May need adjustment if boat grew or hull changed shape</li>
              <li><strong>Suction:</strong> Rock boat side-to-side to break suction seal</li>
              <li><strong>Rollers stuck:</strong> Free up frozen rollers with penetrating oil</li>
            </ul>

            <h2>Preventive Troubleshooting Checklist</h2>

            <p>Perform these checks before every trip to catch problems early:</p>

            <h3>Before Every Trip:</h3>
            <ul>
              <li>✓ Check tire pressure (all tires including spare)</li>
              <li>✓ Inspect tires for damage, cracks, or bulges</li>
              <li>✓ Test all lights (turn signals, brake, running)</li>
              <li>✓ Verify safety chains are attached and not dragging</li>
              <li>✓ Ensure coupler is fully latched with pin installed</li>
              <li>✓ Check trailer brakes are functioning</li>
              <li>✓ Verify load is secure (boat, gear, tie-downs)</li>
            </ul>

            <h3>Monthly Checks:</h3>
            <ul>
              <li>✓ Feel hubs for excessive heat after driving</li>
              <li>✓ Inspect wheel lug nuts for tightness</li>
              <li>✓ Check winch operation and cable/strap condition</li>
              <li>✓ Look for new rust spots or frame cracks</li>
              <li>✓ Test brake function (manual override)</li>
            </ul>

            <h3>Annual Service:</h3>
            <ul>
              <li>✓ Complete wheel bearing repack and inspection</li>
              <li>✓ Brake service and adjustment</li>
              <li>✓ Thorough frame inspection for cracks</li>
              <li>✓ Replace tires over 6 years old (check DOT date code)</li>
              <li>✓ Lubricate all moving parts (jack, winch, rollers)</li>
            </ul>

            <div className="blog-callout">
              <h3>Emergency Kit Essentials</h3>
              <p>Keep these in your tow vehicle for roadside repairs:</p>
              <ul>
                <li>Spare tire (properly inflated)</li>
                <li>Lug wrench and jack</li>
                <li>Wheel bearing grease and basic tools</li>
                <li>Spare bulbs and fuses</li>
                <li>Wire, electrical tape, wire connectors</li>
                <li>Multimeter or test light</li>
                <li>Penetrating oil</li>
                <li>Safety chains</li>
                <li>Coupler lock/hitch pin</li>
                <li>Duct tape and zip ties</li>
              </ul>
            </div>

            <h2>When to Call a Professional</h2>

            <p>Some problems require professional help:</p>
            <ul>
              <li><strong>Structural damage:</strong> Frame cracks, bent axles, damaged welds</li>
              <li><strong>Brake system overhaul:</strong> Complete brake replacement or hydraulic work</li>
              <li><strong>Axle alignment:</strong> Requires specialized equipment</li>
              <li><strong>Major electrical rewiring:</strong> If corrosion is extensive throughout</li>
              <li><strong>Suspension replacement:</strong> Axle or spring replacement</li>
            </ul>

            <p>
              Most trailer problems are preventable with regular maintenance and prompt attention to small issues.
              By learning to recognize and diagnose common problems, you'll spend less time and money on repairs
              and more time enjoying your boat!
            </p>
          </div>
        </article>
      </div>
    </div>
  );
};

export default TroubleshootingPage;
